import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { 
  Edit, Plus, Check, X, FileText, BookOpen, Layers, FolderOpen,
  Database, TrendingUp, Trash2, RefreshCw, Save, 
  Search, Eye, ChevronRight, Zap, Loader, AlertCircle,
  Calendar, Award, Timer, Target, Hash, Type, Info, Upload, Image, FileJson, Download, File, Copy,
  PanelLeftClose, PanelLeft, Globe, BarChart3
} from 'lucide-react';

import { compressImage } from '../lib/compressImage';
import { toast } from 'sonner';
import { renderLatex } from '../lib/renderLatex';

const EditorPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Data
  const [stats, setStats] = useState({});
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [questions, setQuestions] = useState([]);
  
  // Selection state
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  
  // Edit mode
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  
  // Add mode
  const [addingType, setAddingType] = useState(null);
  const [addForm, setAddForm] = useState({});
  
  // Expanded view for questions
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  
  // Bulk import state
  const [bulkImportData, setBulkImportData] = useState('');
  const [importPreview, setImportPreview] = useState([]);
  const [importProgress, setImportProgress] = useState({ uploading: false, current: 0, total: 0 });
  const [uploadedImages, setUploadedImages] = useState([]);
  
  // AI Parser state
  const [aiParserInput, setAiParserInput] = useState('');
  const [aiParsedQuestions, setAiParsedQuestions] = useState([]);
  const [aiProcessing, setAiProcessing] = useState(false);
  
  // PDF Upload state
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfProgress, setPdfProgress] = useState({ status: '', page: 0, total: 0 });
  const [pdfExtractedQuestions, setPdfExtractedQuestions] = useState([]);
  const [autoGenerateSolutions, setAutoGenerateSolutions] = useState(true);
  
  // CSV Upload state
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvImportResult, setCsvImportResult] = useState(null);
  
  // Image Questions state
  const [imageQuestions, setImageQuestions] = useState([]);
  const [currentImageQuestion, setCurrentImageQuestion] = useState({
    question_image: null,
    question_text: '', // Optional text alongside image
    options: [
      { label: 'A', text: '', image: null },
      { label: 'B', text: '', image: null },
      { label: 'C', text: '', image: null },
      { label: 'D', text: '', image: null }
    ],
    correct_answer: 'A',
    explanation: '',
    solution_image: null,
    year: '',
    is_pyq: true,
    is_important: false,
    question_type: 'MCQ'
  });
  
  // Scraper state
  const [scraperUrl, setScraperUrl] = useState('');
  const [scraperLoading, setScraperLoading] = useState(false);
  const [scrapedQuestions, setScrapedQuestions] = useState([]);
  const [fetchBatchProgress, setFetchBatchProgress] = useState({ fetched: 0, total: 0, inProgress: false });

  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (user?.role !== 'content_editor' && user?.role !== 'admin') {
      toast.error('Editor or Admin access required');
      navigate('/dashboard');
      return;
    }
    loadStats();
    loadExams();
  }, [user, navigate]);

  const loadStats = async () => {
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data || {});
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const loadExams = async () => {
    try {
      const res = await api.get('/exams');
      setExams(res.data || []);
    } catch (error) {
      toast.error('Failed to load exams');
    }
  };

  const loadSubjects = async (examId) => {
    try {
      setLoading(true);
      const res = await api.get(`/exams/${examId}/subjects`);
      setSubjects(res.data || []);
      setSelectedExam(examId);
      setSelectedSubject(null);
      setChapters([]);
    } catch (error) {
      toast.error('Failed to load subjects');
    } finally {
      setLoading(false);
    }
  };

  const loadChapters = async (subjectId) => {
    try {
      setLoading(true);
      const res = await api.get(`/subjects/${subjectId}/chapters`);
      setChapters(res.data || []);
      setSelectedSubject(subjectId);
    } catch (error) {
      toast.error('Failed to load chapters');
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async (filters = {}) => {
    try {
      setLoading(true);
      const params = { limit: 500 };
      if (filters.exam_id) params.exam_id = filters.exam_id;
      if (filters.subject_id) params.subject_id = filters.subject_id;
      if (filters.chapter_id) params.chapter_id = filters.chapter_id;
      
      const res = await api.get('/admin/questions', { params });
      setQuestions(res.data || []);
    } catch (error) {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  // ==================== CRUD OPERATIONS ====================

  const handleEdit = (type, item) => {
    setEditingItem({ type, id: item.id });
    // Deep copy for editing
    const formData = JSON.parse(JSON.stringify(item));
    
    // For questions, extract individual options
    if (type === 'questions' && item.options) {
      const opts = item.options;
      formData.optionA = opts.find(o => o.label === 'A')?.text || '';
      formData.optionB = opts.find(o => o.label === 'B')?.text || '';
      formData.optionC = opts.find(o => o.label === 'C')?.text || '';
      formData.optionD = opts.find(o => o.label === 'D')?.text || '';
    }
    
    setEditForm(formData);
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    
    try {
      setLoading(true);
      const { type, id } = editingItem;
      
      let dataToSend = { ...editForm };
      
      // For questions, reconstruct options array
      if (type === 'questions') {
        dataToSend.options = [
          { label: 'A', text: editForm.optionA || '' },
          { label: 'B', text: editForm.optionB || '' },
          { label: 'C', text: editForm.optionC || '' },
          { label: 'D', text: editForm.optionD || '' },
        ];
        delete dataToSend.optionA;
        delete dataToSend.optionB;
        delete dataToSend.optionC;
        delete dataToSend.optionD;
      }
      
      await api.put(`/admin/${type}/${id}`, dataToSend);
      
      toast.success(`Updated successfully!`);
      setEditingItem(null);
      setEditForm({});
      
      // Reload data
      if (type === 'questions') loadQuestions({ exam_id: selectedExam });
      
      loadStats();
    } catch (error) {
      toast.error('Failed to update: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This cannot be undone.`)) return;
    
    try {
      setLoading(true);
      await api.delete(`/admin/${type}/${id}`);
      toast.success(`Deleted successfully!`);
      
      // Reload data
      if (type === 'questions') loadQuestions({ exam_id: selectedExam });
      
      loadStats();
    } catch (error) {
      toast.error('Failed to delete: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!addingType) return;
    
    try {
      setLoading(true);
      
      if (addingType === 'questions') {
        await api.post('/admin/questions', {
          ...addForm,
          exam_id: selectedExam,
          subject_id: selectedSubject || addForm.subject_id,
          chapter_id: selectedChapter || addForm.chapter_id,
          options: [
            { label: 'A', text: addForm.optionA || '' },
            { label: 'B', text: addForm.optionB || '' },
            { label: 'C', text: addForm.optionC || '' },
            { label: 'D', text: addForm.optionD || '' },
          ]
        });
      }
      
      toast.success(`Created successfully!`);
      setAddingType(null);
      setAddForm({});
      
      // Reload data
      if (addingType === 'questions') loadQuestions({ exam_id: selectedExam });
      
      loadStats();
    } catch (error) {
      toast.error('Failed to create: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setEditForm({});
  };

  const cancelAdd = () => {
    setAddingType(null);
    setAddForm({});
  };

  // ==================== BULK IMPORT FUNCTIONS ====================

  const handleJsonParse = () => {
    try {
      const parsed = JSON.parse(bulkImportData);
      const questions = Array.isArray(parsed) ? parsed : [parsed];
      setImportPreview(questions);
      toast.success(`Parsed ${questions.length} questions`);
    } catch (error) {
      toast.error('Invalid JSON: ' + error.message);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.name.endsWith('.json')) {
      const text = await file.text();
      setBulkImportData(text);
      try {
        const parsed = JSON.parse(text);
        const questions = Array.isArray(parsed) ? parsed : [parsed];
        setImportPreview(questions);
        toast.success(`Loaded ${questions.length} questions from file`);
      } catch (error) {
        toast.error('Invalid JSON file');
      }
    } else {
      toast.error('Please upload a JSON file');
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setImportProgress({ uploading: true, current: 0, total: files.length });

    const uploaded = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        // Compress image before upload
        const compressedFile = await compressImage(file);
        const formData = new FormData();
        formData.append('file', compressedFile);
        formData.append('category', 'questions');

        const res = await api.post('/admin/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploaded.push({
          original: file.name,
          url: res.data.url,
          filename: res.data.filename
        });
        setImportProgress(prev => ({ ...prev, current: i + 1 }));
      } catch (error) {
        console.error('Failed to upload:', file.name);
      }
    }

    setUploadedImages(prev => [...prev, ...uploaded]);
    setImportProgress({ uploading: false, current: 0, total: 0 });
    toast.success(`Uploaded ${uploaded.length} images`);
  };

  const handleBulkImport = async () => {
    if (importPreview.length === 0) {
      toast.error('No questions to import');
      return;
    }
    if (!selectedExam) {
      toast.error('Please select an exam first');
      return;
    }

    setLoading(true);
    try {
      // Add exam_id to all questions if not present
      const questionsWithExam = importPreview.map(q => ({
        ...q,
        exam_id: q.exam_id || selectedExam,
        subject_id: q.subject_id || selectedSubject || 'general',
        chapter_id: q.chapter_id || selectedChapter || 'general'
      }));

      const res = await api.post('/admin/questions/bulk', questionsWithExam);
      toast.success(`Imported ${res.data.count} questions!`);
      
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} questions had errors`);
      }

      // Reset
      setBulkImportData('');
      setImportPreview([]);
      loadStats();
    } catch (error) {
      toast.error('Import failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const template = [
      {
        "question_text": "What is the SI unit of force?",
        "options": [
          { "label": "A", "text": "Newton" },
          { "label": "B", "text": "Joule" },
          { "label": "C", "text": "Watt" },
          { "label": "D", "text": "Pascal" }
        ],
        "correct_answer": "A",
        "explanation": "Force = mass × acceleration. The SI unit is Newton (N).",
        "difficulty": "easy",
        "year": 2023,
        "question_type": "MCQ",
        "tags": ["physics", "mechanics"],
        "is_pyq": true,
        "is_important": true,
        "source": "JEE Main 2023",
        "image_url": null,
        "option_images": null,
        "solution_image": null
      }
    ];

    const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'question_template.json';
    a.click();
  };

  // ==================== AI PARSER FUNCTIONS ====================

  const parseQuestionWithAI = async (text) => {
    try {
      setAiProcessing(true);
      const res = await api.post('/admin/ai/parse-question-text', { 
        text, 
        subject: subjects.find(s => s.id === selectedSubject)?.name || 'Physics' 
      });
      return res.data;
    } catch (error) {
      toast.error('Failed to parse: ' + (error.response?.data?.detail || error.message));
      return null;
    } finally {
      setAiProcessing(false);
    }
  };

  const generateSolutionForQuestion = async (question) => {
    try {
      setAiProcessing(true);
      const res = await api.post('/admin/ai/generate-solution', {
        question_text: question.question_text,
        options: question.options,
        subject: subjects.find(s => s.id === selectedSubject)?.name || 'Physics',
        diagram_description: question.diagram_description
      });
      return res.data;
    } catch (error) {
      toast.error('Failed to generate solution: ' + (error.response?.data?.detail || error.message));
      return null;
    } finally {
      setAiProcessing(false);
    }
  };

  const handleParseMultipleQuestions = async () => {
    if (!aiParserInput.trim()) {
      toast.error('Please enter question text');
      return;
    }

    // Split by common separators (Q1, Q2, Q., 1., etc.)
    // eslint-disable-next-line no-useless-escape
    const questionBlocks = aiParserInput.split(/(?=Q\d+[\.\)]|^\d+[\.\)]|\n\n(?=\d+[\.\)]))/gm)
      .filter(block => block.trim().length > 20);

    if (questionBlocks.length === 0) {
      // Try to parse as single question
      const parsed = await parseQuestionWithAI(aiParserInput);
      if (parsed) {
        setAiParsedQuestions([parsed]);
        toast.success('Parsed 1 question');
      }
      return;
    }

    const parsed = [];
    for (let i = 0; i < questionBlocks.length; i++) {
      toast.info(`Parsing question ${i + 1}/${questionBlocks.length}...`);
      const result = await parseQuestionWithAI(questionBlocks[i]);
      if (result) {
        parsed.push(result);
      }
      // Small delay between requests
      await new Promise(r => setTimeout(r, 500));
    }

    setAiParsedQuestions(parsed);
    toast.success(`Parsed ${parsed.length} questions`);
  };

  const handleGenerateAllSolutions = async () => {
    if (aiParsedQuestions.length === 0) {
      toast.error('No questions to process');
      return;
    }

    const updated = [...aiParsedQuestions];
    for (let i = 0; i < updated.length; i++) {
      toast.info(`Generating solution ${i + 1}/${updated.length}...`);
      const solution = await generateSolutionForQuestion(updated[i]);
      if (solution) {
        updated[i] = {
          ...updated[i],
          explanation: solution.explanation,
          correct_answer: solution.correct_answer || updated[i].correct_answer
        };
      }
      await new Promise(r => setTimeout(r, 500));
    }

    setAiParsedQuestions(updated);
    toast.success('Generated all solutions!');
  };

  const handleImportParsedQuestions = async () => {
    if (aiParsedQuestions.length === 0) {
      toast.error('No questions to import');
      return;
    }
    if (!selectedExam) {
      toast.error('Please select an exam');
      return;
    }

    const questionsToImport = aiParsedQuestions.map(q => ({
      ...q,
      exam_id: selectedExam,
      subject_id: selectedSubject || 'general',
      chapter_id: selectedChapter || 'general',
      is_pyq: true,
      question_type: q.question_type || 'MCQ'
    }));

    try {
      setLoading(true);
      const res = await api.post('/admin/questions/bulk', questionsToImport);
      toast.success(`Imported ${res.data.count} questions!`);
      setAiParsedQuestions([]);
      setAiParserInput('');
      loadStats();
    } catch (error) {
      toast.error('Import failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // ==================== QUICK TOGGLE AVAILABILITY ====================
  
  const toggleExamAvailability = async (exam) => {
    try {
      const newStatus = !exam.is_available;
      await api.put(`/admin/exams/${exam.id}`, { is_available: newStatus });
      toast.success(`${exam.name} is now ${newStatus ? 'AVAILABLE' : 'Coming Soon'}`);
      loadExams();
      loadStats();
    } catch (error) {
      toast.error('Failed to toggle availability: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Sidebar navigation config
  const sidebarSections = [
    {
      label: 'Overview',
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: Database, color: 'text-blue-500' },
      ]
    },
    {
      label: 'Content Management',
      items: [
        { id: 'exams', label: 'Exams', icon: BookOpen, color: 'text-blue-500' },
        { id: 'subjects', label: 'Subjects', icon: Layers, color: 'text-green-500' },
        { id: 'chapters', label: 'Chapters', icon: FolderOpen, color: 'text-yellow-500' },
        { id: 'questions', label: 'Questions', icon: FileText, color: 'text-purple-500' },
      ]
    },
    {
      label: 'Import Tools',
      items: [
        { id: 'image-upload', label: 'Image Upload', icon: Image, color: 'text-pink-500' },
        { id: 'scraper', label: 'Web Scraper', icon: Globe, color: 'text-cyan-500' },
        { id: 'pdf-upload', label: 'PDF / CSV Import', icon: File, color: 'text-red-500' },
        { id: 'bulk-import', label: 'Bulk Import', icon: Upload, color: 'text-indigo-500' },
        { id: 'ai-parser', label: 'AI Parser', icon: Zap, color: 'text-amber-500' },
      ]
    },
  ];

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    if (tabId === 'questions' && selectedExam) loadQuestions({ exam_id: selectedExam });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExam]);

  if (user?.role !== 'content_editor' && user?.role !== 'admin') return null;

  // ==================== FIELD RENDERERS ====================

  const renderField = (label, field, form, setFormFn, type = 'text', options = null, icon = null) => {
    const Icon = icon;
    return (
      <div className="mb-4">
        <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
          {Icon && <Icon size={14} className="text-muted-foreground" />}
          {label}
        </label>
        {options ? (
          <select
            value={form[field] || ''}
            onChange={(e) => setFormFn({ ...form, [field]: e.target.value })}
            className="w-full p-3 rounded-lg bg-background border border-border text-sm focus:ring-2 focus:ring-primary focus:border-primary transition-all"
          >
            <option value="">Select {label}</option>
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : type === 'textarea' ? (
          <textarea
            value={form[field] || ''}
            onChange={(e) => setFormFn({ ...form, [field]: e.target.value })}
            className="w-full p-3 rounded-lg bg-background border border-border text-sm min-h-[100px] focus:ring-2 focus:ring-primary focus:border-primary transition-all"
            placeholder={`Enter ${label.toLowerCase()}`}
          />
        ) : type === 'checkbox' ? (
          <input
            type="checkbox"
            checked={form[field] || false}
            onChange={(e) => setFormFn({ ...form, [field]: e.target.checked })}
            className="w-5 h-5 rounded border-border"
          />
        ) : (
          <Input
            type={type}
            value={form[field] ?? ''}
            onChange={(e) => setFormFn({ ...form, [field]: type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value })}
            className="text-sm"
            placeholder={`Enter ${label.toLowerCase()}`}
          />
        )}
      </div>
    );
  };

  const difficultyOptions = [
    { value: 'easy', label: '🟢 Easy' },
    { value: 'medium', label: '🟡 Medium' },
    { value: 'hard', label: '🔴 Hard' },
  ];

  const answerOptions = [
    { value: 'A', label: 'A' },
    { value: 'B', label: 'B' },
    { value: 'C', label: 'C' },
    { value: 'D', label: 'D' },
  ];

  const categoryOptions = [
    { value: 'Engineering', label: 'Engineering' },
    { value: 'Medical', label: 'Medical' },
    { value: 'Intermediate', label: 'Intermediate' },
    { value: 'School', label: 'School' },
  ];

  const levelOptions = [
    { value: 'National', label: 'National' },
    { value: 'State', label: 'State' },
    { value: 'School', label: 'School' },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-background flex">
        {/* ====== SIDEBAR ====== */}
        <aside className={`${sidebarCollapsed ? 'w-16' : 'w-60'} flex-shrink-0 border-r border-border bg-card/50 transition-all duration-200 hidden md:flex flex-col`}>
          {/* Sidebar Header */}
          <div className="p-4 border-b border-border flex items-center justify-between">
            {!sidebarCollapsed && (
              <div className="flex items-center gap-2">
                <Edit className="text-primary" size={20} />
                <h2 className="font-bold text-foreground">Editor Panel</h2>
              </div>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors"
            >
              {sidebarCollapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
            </button>
          </div>

          {/* Sidebar Navigation */}
          <nav className="flex-1 overflow-y-auto p-3 space-y-6">
            {sidebarSections.map((section) => (
              <div key={section.label}>
                {!sidebarCollapsed && (
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-3">
                    {section.label}
                  </p>
                )}
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleTabChange(item.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                          isActive 
                            ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20' 
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        } ${sidebarCollapsed ? 'justify-center px-2' : ''}`}
                        title={sidebarCollapsed ? item.label : undefined}
                      >
                        <Icon size={18} className={!isActive ? item.color : ''} />
                        {!sidebarCollapsed && <span>{item.label}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Sidebar Stats (compact) */}
          {!sidebarCollapsed && (
            <div className="p-4 border-t border-border">
              <div className="text-xs space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Questions</span>
                  <span className="font-bold text-foreground">{stats.total_questions || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Exams</span>
                  <span className="font-bold text-foreground">{stats.total_exams || 0}</span>
                </div>
              </div>
            </div>
          )}
        </aside>

        {/* ====== MAIN CONTENT ====== */}
        <main className="flex-1 overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-card/80 backdrop-blur-xl border-b border-border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-bold text-foreground capitalize">
                  {activeTab === 'image-upload' ? 'Image Upload' : 
                   activeTab === 'pdf-upload' ? 'PDF / CSV Import' :
                   activeTab === 'bulk-import' ? 'Bulk Import' :
                   activeTab === 'ai-parser' ? 'AI Parser' : activeTab}
                </h1>
                {selectedExam && activeTab !== 'dashboard' && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <ChevronRight size={14} />
                    <span className="text-primary">{exams.find(e => e.id === selectedExam)?.name}</span>
                    {selectedSubject && (
                      <><ChevronRight size={14} /><span>{subjects.find(s => s.id === selectedSubject)?.name}</span></>
                    )}
                    {selectedChapter && (
                      <><ChevronRight size={14} /><span>{chapters.find(c => c.id === selectedChapter)?.name}</span></>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={loadStats} title="Refresh stats">
                  <RefreshCw size={14} />
                </Button>
                <Badge variant="outline" className="text-xs border-yellow-500/50 text-yellow-400">
                  Editor
                </Badge>
                <Badge variant="outline" className="text-xs">{user?.email}</Badge>
              </div>
            </div>
          </div>

          {/* Page Content */}
          <div className="p-6 pb-20 md:pb-6">

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { icon: FileText, label: 'Questions', value: stats.total_questions || 0, color: 'text-purple-500', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
                { icon: BookOpen, label: 'Exams', value: stats.total_exams || 0, color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
                { icon: Layers, label: 'Subjects', value: stats.total_subjects || 0, color: 'text-green-500', bg: 'bg-green-500/10', border: 'border-green-500/20' },
                { icon: FolderOpen, label: 'Chapters', value: stats.total_chapters || 0, color: 'text-yellow-500', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
              ].map((stat, idx) => {
                const Icon = stat.icon;
                return (
                  <Card key={idx} className={`p-4 rounded-xl border ${stat.border} ${stat.bg} hover:scale-[1.02] transition-transform`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={stat.color} size={16} />
                      <span className="text-xs text-muted-foreground">{stat.label}</span>
                    </div>
                    <p className="text-2xl font-bold text-foreground">{stat.value.toLocaleString()}</p>
                  </Card>
                );
              })}
            </div>

            {/* Quick Actions */}
            <Card className="p-5 border border-border">
              <h3 className="font-bold text-foreground mb-3 flex items-center gap-2 text-sm">
                <Zap size={18} className="text-amber-500" />
                Quick Actions
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Button size="sm" onClick={() => handleTabChange('exams')} className="bg-blue-500 hover:bg-blue-600 text-xs">
                  <BookOpen size={14} className="mr-1" /> Exams
                </Button>
                <Button size="sm" onClick={() => handleTabChange('subjects')} className="bg-green-500 hover:bg-green-600 text-xs">
                  <Layers size={14} className="mr-1" /> Subjects
                </Button>
                <Button size="sm" onClick={() => handleTabChange('chapters')} className="bg-yellow-500 hover:bg-yellow-600 text-xs text-black">
                  <FolderOpen size={14} className="mr-1" /> Chapters
                </Button>
                <Button size="sm" onClick={() => handleTabChange('questions')} className="bg-purple-500 hover:bg-purple-600 text-xs">
                  <FileText size={14} className="mr-1" /> Questions
                </Button>
                <Button size="sm" onClick={() => handleTabChange('image-upload')} className="bg-pink-500 hover:bg-pink-600 text-xs">
                  <Image size={14} className="mr-1" /> Image Upload
                </Button>
                <Button size="sm" onClick={() => handleTabChange('scraper')} className="bg-cyan-500 hover:bg-cyan-600 text-xs">
                  <Globe size={14} className="mr-1" /> Web Scraper
                </Button>
                <Button size="sm" onClick={() => handleTabChange('pdf-upload')} className="bg-red-500 hover:bg-red-600 text-xs">
                  <File size={14} className="mr-1" /> PDF Import
                </Button>
                <Button size="sm" onClick={() => handleTabChange('ai-parser')} className="bg-amber-500 hover:bg-amber-600 text-xs">
                  <Zap size={14} className="mr-1" /> AI Parser
                </Button>
              </div>
            </Card>
            
            {/* Workflow Guide */}
            <Card className="p-5 border border-border">
              <h3 className="font-bold text-foreground mb-3 flex items-center gap-2 text-sm">
                <Info size={18} className="text-blue-500" />
                Content Editor Workflow
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
                {[
                  { step: '1', label: 'Select Exam', desc: 'Choose exam & subject', color: 'blue' },
                  { step: '2', label: 'Import Questions', desc: 'Scrape, PDF, or manual', color: 'purple' },
                  { step: '3', label: 'Review Quality', desc: 'Edit & verify accuracy', color: 'green' },
                  { step: '4', label: 'Publish', desc: 'Questions go live', color: 'cyan' },
                ].map((s) => (
                  <div key={s.step} className={`p-3 rounded-lg bg-${s.color}-500/10 border border-${s.color}-500/20 text-center`}>
                    <div className={`w-6 h-6 rounded-full bg-${s.color}-500 text-white text-xs font-bold flex items-center justify-center mx-auto mb-1.5`}>{s.step}</div>
                    <p className="font-semibold text-foreground">{s.label}</p>
                    <p className="text-muted-foreground mt-0.5">{s.desc}</p>
                  </div>
                ))}
              </div>
            </Card>

            {/* Current Selection */}
            {(selectedExam || selectedSubject || selectedChapter) && (
              <Card className="p-4 border border-border">
                <h4 className="font-medium text-foreground text-sm mb-2">Active Selection</h4>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {selectedExam && <Badge variant="outline"><BookOpen size={12} className="mr-1" />{exams.find(e => e.id === selectedExam)?.name}</Badge>}
                  {selectedSubject && <><ChevronRight size={14} /><Badge variant="outline"><Layers size={12} className="mr-1" />{subjects.find(s => s.id === selectedSubject)?.name}</Badge></>}
                  {selectedChapter && <><ChevronRight size={14} /><Badge variant="outline"><FolderOpen size={12} className="mr-1" />{chapters.find(c => c.id === selectedChapter)?.name}</Badge></>}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Exams Tab */}
        {activeTab === 'exams' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center flex-wrap gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search exams by name or category..."
                  className="pl-10"
                />
              </div>
              <Button onClick={() => { setAddingType('exams'); setAddForm({}); }} className="bg-blue-500 hover:bg-blue-600">
                <Plus size={16} className="mr-2" /> Add Exam
              </Button>
              <Button variant="outline" onClick={loadExams}>
                <RefreshCw size={16} />
              </Button>
            </div>

            {/* Add Exam Form */}
            {addingType === 'exams' && (
              <Card className="p-6 bg-green-500/5 border-green-500/30 mb-4">
                <h4 className="font-bold text-green-500 mb-4 flex items-center gap-2">
                  <Plus size={20} /> Add New Exam - All Fields
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderField('Exam Name', 'name', addForm, setAddForm, 'text', null, Type)}
                  {renderField('Full Name', 'full_name', addForm, setAddForm, 'text', null, Info)}
                  {renderField('Icon (emoji)', 'icon', addForm, setAddForm, 'text', null, Award)}
                  {renderField('Category', 'category', addForm, setAddForm, 'text', categoryOptions, FolderOpen)}
                  {renderField('Level', 'level', addForm, setAddForm, 'text', levelOptions, Target)}
                  {renderField('Total Questions', 'total_questions', addForm, setAddForm, 'number', null, Hash)}
                  {renderField('Duration (minutes)', 'duration_minutes', addForm, setAddForm, 'number', null, Timer)}
                  {renderField('Marks per Correct', 'marking_correct', addForm, setAddForm, 'number', null, Check)}
                  {renderField('Marks per Wrong (negative)', 'marking_incorrect', addForm, setAddForm, 'number', null, X)}
                  {renderField('Total Marks', 'total_marks', addForm, setAddForm, 'number', null, Award)}
                </div>
                {renderField('Description', 'description', addForm, setAddForm, 'textarea', null, Info)}
                <div className="flex items-center gap-3 mt-3 p-3 bg-background rounded-lg border border-border">
                  <label className="text-sm font-medium text-foreground flex items-center gap-2 cursor-pointer select-none">
                    <div className={`relative w-11 h-6 rounded-full transition-colors ${addForm.is_available ? 'bg-green-500' : 'bg-gray-600'}`}
                      onClick={() => setAddForm(prev => ({ ...prev, is_available: !prev.is_available }))}
                    >
                      <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${addForm.is_available ? 'translate-x-5' : ''}`} />
                    </div>
                    <span className={addForm.is_available ? 'text-green-500 font-semibold' : 'text-muted-foreground'}>
                      {addForm.is_available ? 'Available Now' : 'Coming Soon'}
                    </span>
                  </label>
                  <span className="text-xs text-muted-foreground">Toggle to make this exam available for students immediately</span>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={handleAdd} disabled={loading || !addForm.name} className="bg-green-500 hover:bg-green-600">
                    {loading ? <Loader size={16} className="animate-spin mr-2" /> : <Check size={16} className="mr-2" />}
                    Create Exam
                  </Button>
                  <Button variant="outline" onClick={cancelAdd}><X size={16} className="mr-2" /> Cancel</Button>
                </div>
              </Card>
            )}

            {/* Exams List */}
            <div className="grid gap-3">
              {exams.filter(exam => 
                searchTerm === '' || 
                exam.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                exam.category?.toLowerCase().includes(searchTerm.toLowerCase())
              ).map(exam => (
                <Card key={exam.id} className={`p-4 border transition-all ${selectedExam === exam.id ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'border-border hover:border-primary/50'}`}>
                  {editingItem?.type === 'exams' && editingItem?.id === exam.id ? (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-bold text-amber-500 flex items-center gap-2">
                          <Edit size={20} /> Editing: {exam.name}
                        </h4>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={handleSaveEdit} disabled={loading} className="bg-green-500 hover:bg-green-600">
                            {loading ? <Loader size={16} className="animate-spin mr-1" /> : <Save size={16} className="mr-1" />}
                            Save All
                          </Button>
                          <Button size="sm" variant="outline" onClick={cancelEdit}><X size={16} /></Button>
                        </div>
                      </div>

                      {/* Availability Toggle */}
                      <div className="flex items-center gap-3 p-3 mb-3 rounded-lg border border-border bg-background">
                        <label className="text-sm font-medium text-foreground flex items-center gap-2 cursor-pointer select-none">
                          <div className={`relative w-11 h-6 rounded-full transition-colors ${editForm.is_available ? 'bg-green-500' : 'bg-gray-600'}`}
                            onClick={() => setEditForm(prev => ({ ...prev, is_available: !prev.is_available }))}
                          >
                            <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${editForm.is_available ? 'translate-x-5' : ''}`} />
                          </div>
                          <span className={editForm.is_available ? 'text-green-500 font-semibold' : 'text-muted-foreground'}>
                            {editForm.is_available ? 'Available Now' : 'Coming Soon'}
                          </span>
                        </label>
                        <span className="text-xs text-muted-foreground">
                          {editForm.is_available ? 'Students can practice and take tests for this exam' : 'Exam appears as "Coming Soon" on all pages'}
                        </span>
                      </div>

                      {/* Basic Info Section */}
                      <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg mb-3">
                        <p className="text-xs font-semibold text-blue-500 mb-3 uppercase tracking-wider">Basic Information</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {renderField('Exam Name', 'name', editForm, setEditForm, 'text', null, Type)}
                          {renderField('Full Name', 'full_name', editForm, setEditForm, 'text', null, Info)}
                          {renderField('Icon (emoji)', 'icon', editForm, setEditForm, 'text', null, Award)}
                          {renderField('Category', 'category', editForm, setEditForm, 'text', categoryOptions, FolderOpen)}
                          {renderField('Level', 'level', editForm, setEditForm, 'text', levelOptions, Target)}
                        </div>
                        {renderField('Description', 'description', editForm, setEditForm, 'textarea', null, Info)}
                      </div>

                      {/* Exam Pattern Section */}
                      <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg mb-3">
                        <p className="text-xs font-semibold text-purple-500 mb-3 uppercase tracking-wider">Exam Pattern & Marking</p>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                          {renderField('Total Questions', 'total_questions', editForm, setEditForm, 'number', null, Hash)}
                          {renderField('Duration (min)', 'duration_minutes', editForm, setEditForm, 'number', null, Timer)}
                          {renderField('Marks/Correct', 'marking_correct', editForm, setEditForm, 'number', null, Check)}
                          {renderField('Marks/Wrong', 'marking_incorrect', editForm, setEditForm, 'number', null, X)}
                          {renderField('Total Marks', 'total_marks', editForm, setEditForm, 'number', null, Award)}
                        </div>
                      </div>

                      <div className="flex gap-2 mt-4 pt-3 border-t border-border">
                        <Button onClick={handleSaveEdit} disabled={loading} className="bg-green-500 hover:bg-green-600">
                          {loading ? <Loader size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                          Save All Changes
                        </Button>
                        <Button variant="outline" onClick={cancelEdit}><X size={16} className="mr-2" /> Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div 
                        className="flex items-center gap-3 cursor-pointer flex-1" 
                        onClick={() => { loadSubjects(exam.id); setSelectedSubject(null); setChapters([]); }}
                      >
                        <span className="text-3xl">{exam.icon || '📚'}</span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-foreground">{exam.name}</h3>
                            {exam.full_name && <span className="text-xs text-muted-foreground hidden md:inline">({exam.full_name})</span>}
                          </div>
                          <div className="flex flex-wrap gap-2 mt-1">
                            <Badge 
                              variant="outline" 
                              className={`text-xs cursor-pointer select-none ${exam.is_available ? 'bg-green-500/10 text-green-500 border-green-500/30 hover:bg-green-500/20' : 'bg-amber-500/10 text-amber-500 border-amber-500/30 hover:bg-amber-500/20'}`}
                              onClick={(e) => { e.stopPropagation(); toggleExamAvailability(exam); }}
                              title={exam.is_available ? 'Click to mark as Coming Soon' : 'Click to make Available'}
                            >
                              {exam.is_available ? '✓ Available' : '⏳ Coming Soon'}
                            </Badge>
                            <Badge variant="outline" className="text-xs">{exam.category}</Badge>
                            <Badge variant="outline" className="text-xs">{exam.level}</Badge>
                            <Badge variant="outline" className="text-xs">{exam.total_questions} Q</Badge>
                            <Badge variant="outline" className="text-xs">{exam.duration_minutes} min</Badge>
                            <Badge variant="outline" className="text-xs text-green-500">+{exam.marking_correct}</Badge>
                            <Badge variant="outline" className="text-xs text-red-500">{exam.marking_incorrect}</Badge>
                          </div>
                        </div>
                        {selectedExam === exam.id && <ChevronRight size={20} className="text-primary" />}
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleEdit('exams', exam); }}>
                          <Edit size={14} className="mr-1" /> Edit
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Subjects Tab */}
        {activeTab === 'subjects' && (
          <div className="space-y-4">
            {!selectedExam ? (
              <Card className="p-8 text-center border-dashed border-2">
                <AlertCircle size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium text-foreground mb-2">No Exam Selected</p>
                <p className="text-muted-foreground mb-4">Please select an exam first from the Exams tab by clicking on it</p>
                <Button onClick={() => setActiveTab('exams')}>
                  <BookOpen size={16} className="mr-2" /> Go to Exams
                </Button>
              </Card>
            ) : (
              <>
                <div className="flex justify-between items-center flex-wrap gap-4">
                  <div>
                    <h3 className="font-bold text-foreground text-lg">Subjects for: {exams.find(e => e.id === selectedExam)?.name}</h3>
                    <p className="text-sm text-muted-foreground">{subjects.length} subjects found</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => { setAddingType('subjects'); setAddForm({}); }} className="bg-green-500 hover:bg-green-600">
                      <Plus size={16} className="mr-2" /> Add Subject
                    </Button>
                    <Button variant="outline" onClick={() => loadSubjects(selectedExam)}>
                      <RefreshCw size={16} />
                    </Button>
                  </div>
                </div>

                {addingType === 'subjects' && (
                  <Card className="p-6 bg-green-500/5 border-green-500/30">
                    <h4 className="font-bold text-green-500 mb-4 flex items-center gap-2">
                      <Plus size={20} /> Add New Subject
                    </h4>
                    {renderField('Subject Name', 'name', addForm, setAddForm, 'text', null, Type)}
                    <div className="grid grid-cols-2 gap-4">
                      {renderField('Questions Count', 'questions_count', addForm, setAddForm, 'number', 'Number of questions in this section', Hash)}
                      {renderField('Total Marks', 'marks', addForm, setAddForm, 'number', 'Total marks for this section', Award)}
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button onClick={handleAdd} disabled={loading || !addForm.name} className="bg-green-500 hover:bg-green-600">
                        {loading ? <Loader size={16} className="animate-spin mr-2" /> : <Check size={16} className="mr-2" />}
                        Create Subject
                      </Button>
                      <Button variant="outline" onClick={cancelAdd}><X size={16} className="mr-2" /> Cancel</Button>
                    </div>
                  </Card>
                )}

                <div className="grid gap-3">
                  {subjects.map(subject => (
                    <Card key={subject.id} className={`p-4 border transition-all ${selectedSubject === subject.id ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'border-border hover:border-primary/50'}`}>
                      {editingItem?.type === 'subjects' && editingItem?.id === subject.id ? (
                        <div>
                          <h4 className="font-bold text-amber-500 mb-4 flex items-center gap-2">
                            <Edit size={20} /> Editing: {subject.name}
                          </h4>
                          {renderField('Subject Name', 'name', editForm, setEditForm, 'text', null, Type)}
                          <div className="grid grid-cols-2 gap-4">
                            {renderField('Questions Count', 'questions_count', editForm, setEditForm, 'number', null, Hash)}
                            {renderField('Total Marks', 'marks', editForm, setEditForm, 'number', null, Award)}
                          </div>
                          <div className="flex gap-2 mt-4">
                            <Button onClick={handleSaveEdit} disabled={loading} className="bg-green-500 hover:bg-green-600">
                              <Save size={16} className="mr-2" /> Save
                            </Button>
                            <Button variant="outline" onClick={cancelEdit}><X size={16} className="mr-2" /> Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div 
                            className="flex items-center gap-3 cursor-pointer flex-1" 
                            onClick={() => loadChapters(subject.id)}
                          >
                            <Layers size={24} className="text-green-500" />
                            <div>
                              <span className="font-medium text-foreground">{subject.name}</span>
                              <div className="flex gap-3 text-xs text-muted-foreground mt-1">
                                <span>{subject.questions_count || 25} Qs</span>
                                <span>{subject.marks || 100} marks</span>
                              </div>
                            </div>
                            {selectedSubject === subject.id && <ChevronRight size={20} className="text-primary" />}
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleEdit('subjects', subject); }}>
                              <Edit size={14} className="mr-1" /> Edit
                            </Button>
                          </div>
                        </div>
                      )}
                    </Card>
                  ))}
                  {subjects.length === 0 && (
                    <Card className="p-8 text-center border-dashed">
                      <p className="text-muted-foreground">No subjects yet. Add one above!</p>
                    </Card>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Chapters Tab */}
        {activeTab === 'chapters' && (
          <div className="space-y-4">
            {!selectedSubject ? (
              <Card className="p-8 text-center border-dashed border-2">
                <AlertCircle size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium text-foreground mb-2">No Subject Selected</p>
                <p className="text-muted-foreground mb-4">Please select an exam first, then select a subject by clicking on it</p>
                <Button onClick={() => setActiveTab('subjects')}>
                  <Layers size={16} className="mr-2" /> Go to Subjects
                </Button>
              </Card>
            ) : (
              <>
                <div className="flex justify-between items-center flex-wrap gap-4">
                  <div>
                    <h3 className="font-bold text-foreground text-lg">Chapters for: {subjects.find(s => s.id === selectedSubject)?.name}</h3>
                    <p className="text-sm text-muted-foreground">{chapters.length} chapters found</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => { setAddingType('chapters'); setAddForm({}); }} className="bg-yellow-500 hover:bg-yellow-600 text-black">
                      <Plus size={16} className="mr-2" /> Add Chapter
                    </Button>
                    <Button variant="outline" onClick={() => loadChapters(selectedSubject)}>
                      <RefreshCw size={16} />
                    </Button>
                  </div>
                </div>

                {addingType === 'chapters' && (
                  <Card className="p-6 bg-yellow-500/5 border-yellow-500/30">
                    <h4 className="font-bold text-yellow-600 mb-4 flex items-center gap-2">
                      <Plus size={20} /> Add New Chapter
                    </h4>
                    {renderField('Chapter Name', 'name', addForm, setAddForm, 'text', null, Type)}
                    {renderField('Notes Content (optional)', 'notes_content', addForm, setAddForm, 'textarea', null, FileText)}
                    <div className="flex gap-2 mt-4">
                      <Button onClick={handleAdd} disabled={loading || !addForm.name} className="bg-yellow-500 hover:bg-yellow-600 text-black">
                        <Check size={16} className="mr-2" /> Create Chapter
                      </Button>
                      <Button variant="outline" onClick={cancelAdd}><X size={16} className="mr-2" /> Cancel</Button>
                    </div>
                  </Card>
                )}

                <div className="grid gap-3">
                  {chapters.map(chapter => (
                    <Card key={chapter.id} className={`p-4 border transition-all ${selectedChapter === chapter.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
                      {editingItem?.type === 'chapters' && editingItem?.id === chapter.id ? (
                        <div>
                          <h4 className="font-bold text-amber-500 mb-4 flex items-center gap-2">
                            <Edit size={20} /> Editing: {chapter.name}
                          </h4>
                          {renderField('Chapter Name', 'name', editForm, setEditForm, 'text', null, Type)}
                          {renderField('Notes Content', 'notes_content', editForm, setEditForm, 'textarea', null, FileText)}
                          <div className="flex gap-2 mt-4">
                            <Button onClick={handleSaveEdit} disabled={loading} className="bg-green-500 hover:bg-green-600">
                              <Save size={16} className="mr-2" /> Save
                            </Button>
                            <Button variant="outline" onClick={cancelEdit}><X size={16} className="mr-2" /> Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div 
                            className="flex items-center gap-3 cursor-pointer flex-1"
                            onClick={() => setSelectedChapter(chapter.id)}
                          >
                            <FolderOpen size={24} className="text-yellow-500" />
                            <div>
                              <span className="font-medium text-foreground">{chapter.name}</span>
                              {chapter.notes_content && (
                                <p className="text-xs text-muted-foreground mt-1">Has notes content</p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleEdit('chapters', chapter); }}>
                              <Edit size={14} className="mr-1" /> Edit
                            </Button>
                          </div>
                        </div>
                      )}
                    </Card>
                  ))}
                  {chapters.length === 0 && (
                    <Card className="p-8 text-center border-dashed">
                      <p className="text-muted-foreground">No chapters yet. Add one above!</p>
                    </Card>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Questions Tab */}
        {activeTab === 'questions' && (
          <div className="space-y-4">
            <Card className="p-4 bg-card border border-border">
              <h4 className="font-medium text-foreground mb-3">Filter Questions</h4>
              <div className="flex gap-4 items-center flex-wrap">
                <select
                  value={selectedExam || ''}
                  onChange={(e) => { 
                    const examId = e.target.value;
                    setSelectedExam(examId); 
                    if (examId) {
                      loadSubjects(examId); 
                      loadQuestions({ exam_id: examId }); 
                    } else {
                      setSubjects([]);
                      setQuestions([]);
                    }
                    setSelectedSubject(null);
                  }}
                  className="p-3 rounded-lg bg-background border border-border text-sm min-w-[200px]"
                >
                  <option value="">Select Exam</option>
                  {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
                <select
                  value={selectedSubject || ''}
                  onChange={(e) => { 
                    const subjectId = e.target.value;
                    setSelectedSubject(subjectId); 
                    loadQuestions({ exam_id: selectedExam, subject_id: subjectId || undefined }); 
                  }}
                  className="p-3 rounded-lg bg-background border border-border text-sm min-w-[200px]"
                  disabled={!selectedExam}
                >
                  <option value="">All Subjects</option>
                  {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <Button 
                  onClick={() => { setAddingType('questions'); setAddForm({ difficulty: 'medium' }); }} 
                  disabled={!selectedExam} 
                  className="bg-purple-500 hover:bg-purple-600"
                >
                  <Plus size={16} className="mr-2" /> Add Question
                </Button>
                <Button variant="outline" onClick={() => loadQuestions({ exam_id: selectedExam, subject_id: selectedSubject })}>
                  <RefreshCw size={16} className="mr-2" /> Refresh
                </Button>
              </div>
            </Card>

            {addingType === 'questions' && (
              <Card className="p-6 bg-purple-500/5 border-purple-500/30">
                <h4 className="font-bold text-purple-500 mb-4 flex items-center gap-2">
                  <Plus size={20} /> Add New Question
                </h4>
                {renderField('Question Text', 'question_text', addForm, setAddForm, 'textarea', null, FileText)}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderField('Option A', 'optionA', addForm, setAddForm, 'text', null)}
                  {renderField('Option B', 'optionB', addForm, setAddForm, 'text', null)}
                  {renderField('Option C', 'optionC', addForm, setAddForm, 'text', null)}
                  {renderField('Option D', 'optionD', addForm, setAddForm, 'text', null)}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {renderField('Correct Answer', 'correct_answer', addForm, setAddForm, 'text', answerOptions, Check)}
                  {renderField('Difficulty', 'difficulty', addForm, setAddForm, 'text', difficultyOptions, Target)}
                  {renderField('Year', 'year', addForm, setAddForm, 'number', null, Calendar)}
                </div>
                {renderField('Explanation', 'explanation', addForm, setAddForm, 'textarea', null, Info)}
                <div className="flex gap-2 mt-4">
                  <Button onClick={handleAdd} disabled={loading || !addForm.question_text || !addForm.correct_answer} className="bg-purple-500 hover:bg-purple-600">
                    {loading ? <Loader size={16} className="animate-spin mr-2" /> : <Check size={16} className="mr-2" />}
                    Create Question
                  </Button>
                  <Button variant="outline" onClick={cancelAdd}><X size={16} className="mr-2" /> Cancel</Button>
                </div>
              </Card>
            )}

            <p className="text-sm text-muted-foreground">{questions.length} questions loaded {questions.length > 500 && '(showing first 500)'}</p>

            <div className="space-y-3">
              {questions.slice(0, 500).map((q, idx) => (
                <Card key={q.id} className="p-4 border border-border">
                  {editingItem?.type === 'questions' && editingItem?.id === q.id ? (
                    <div>
                      <h4 className="font-bold text-amber-500 mb-4 flex items-center gap-2">
                        <Edit size={20} /> Editing Question #{idx + 1}
                      </h4>
                      {renderField('Question Text', 'question_text', editForm, setEditForm, 'textarea', null, FileText)}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {renderField('Option A', 'optionA', editForm, setEditForm, 'text', null)}
                        {renderField('Option B', 'optionB', editForm, setEditForm, 'text', null)}
                        {renderField('Option C', 'optionC', editForm, setEditForm, 'text', null)}
                        {renderField('Option D', 'optionD', editForm, setEditForm, 'text', null)}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {renderField('Correct Answer', 'correct_answer', editForm, setEditForm, 'text', answerOptions, Check)}
                        {renderField('Difficulty', 'difficulty', editForm, setEditForm, 'text', difficultyOptions, Target)}
                        {renderField('Year', 'year', editForm, setEditForm, 'number', null, Calendar)}
                      </div>
                      {renderField('Explanation', 'explanation', editForm, setEditForm, 'textarea', null, Info)}
                      <div className="flex gap-2 mt-4">
                        <Button onClick={handleSaveEdit} disabled={loading} className="bg-green-500 hover:bg-green-600">
                          {loading ? <Loader size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                          Save Changes
                        </Button>
                        <Button variant="outline" onClick={cancelEdit}><X size={16} className="mr-2" /> Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className="bg-purple-500/20 text-purple-500 text-xs">#{idx + 1}</Badge>
                          <Badge variant="outline" className={`text-xs ${q.difficulty === 'easy' ? 'text-green-500' : q.difficulty === 'hard' ? 'text-red-500' : 'text-yellow-500'}`}>
                            {q.difficulty}
                          </Badge>
                          {q.year && <Badge variant="outline" className="text-xs">{q.year}</Badge>}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => setExpandedQuestion(expandedQuestion === q.id ? null : q.id)}>
                            <Eye size={14} className="mr-1" /> {expandedQuestion === q.id ? 'Less' : 'More'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleEdit('questions', q)}>
                            <Edit size={14} className="mr-1" /> Edit
                          </Button>
                        </div>
                      </div>
                      <p className="font-medium text-foreground mb-2">
                        {expandedQuestion === q.id ? q.question_text : (q.question_text?.slice(0, 150) + (q.question_text?.length > 150 ? '...' : ''))}
                      </p>
                      {expandedQuestion === q.id && (
                        <div className="mt-4 space-y-3 p-4 bg-muted/30 rounded-lg">
                          <div className="grid grid-cols-2 gap-2">
                            {q.options?.map((opt, i) => (
                              <div key={i} className={`p-2 rounded border ${opt.label === q.correct_answer ? 'border-green-500 bg-green-500/10' : 'border-border'}`}>
                                <span className="font-bold">{opt.label}.</span> <span dangerouslySetInnerHTML={{ __html: renderLatex(opt.text) }} />
                              </div>
                            ))}
                          </div>
                          <div className="text-sm">
                            <p><strong className="text-green-500">Correct Answer:</strong> {q.correct_answer}</p>
                            {q.explanation && <p className="mt-2"><strong>Explanation:</strong> {q.explanation}</p>}
                          </div>
                        </div>
                      )}
                      {!expandedQuestion && (
                        <p className="text-xs text-green-500 mt-2">Answer: {q.correct_answer}</p>
                      )}
                    </div>
                  )}
                </Card>
              ))}
              {questions.length === 0 && selectedExam && (
                <Card className="p-8 text-center border-dashed">
                  <p className="text-muted-foreground">No questions found for this exam. Add one above!</p>
                </Card>
              )}
              {!selectedExam && (
                <Card className="p-8 text-center border-dashed">
                  <p className="text-muted-foreground">Select an exam to view and manage its questions</p>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Image Questions Upload Tab - Continuing in next part due to length */}
        {activeTab === 'image-upload' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-start flex-wrap gap-4">
              <div>
                <h3 className="font-bold text-foreground text-xl flex items-center gap-2">
                  <Image className="text-purple-500" size={24} />
                  Image-Based Questions
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Upload screenshots - <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Ctrl+V</kbd> to paste from clipboard!
                </p>
              </div>
              <Badge variant="outline" className="text-green-400 border-green-500/50">
                Session: {imageQuestions.length} added
              </Badge>
            </div>

            {/* Step 1: Select Exam, Subject, Chapter */}
            <Card className="p-6 border-purple-500/30 bg-purple-500/5">
              <h4 className="font-semibold text-purple-400 mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-sm">1</span>
                Select Location
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Exam</label>
                  <select
                    value={selectedExam || ''}
                    onChange={(e) => {
                      setSelectedExam(e.target.value);
                      setSelectedSubject(null);
                      setSelectedChapter(null);
                      if (e.target.value) {
                        api.get(`/exams/${e.target.value}/subjects`).then(res => setSubjects(res.data || []));
                      }
                    }}
                    className="w-full p-3 rounded-xl border border-border bg-background text-foreground"
                  >
                    <option value="">Select Exam</option>
                    {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Subject</label>
                  <select
                    value={selectedSubject || ''}
                    onChange={(e) => {
                      setSelectedSubject(e.target.value);
                      setSelectedChapter(null);
                      if (e.target.value) {
                        api.get(`/subjects/${e.target.value}/chapters`).then(res => setChapters(res.data || []));
                      }
                    }}
                    disabled={!selectedExam}
                    className="w-full p-3 rounded-xl border border-border bg-background text-foreground disabled:opacity-50"
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Chapter</label>
                  <select
                    value={selectedChapter || ''}
                    onChange={(e) => setSelectedChapter(e.target.value)}
                    disabled={!selectedSubject}
                    className="w-full p-3 rounded-xl border border-border bg-background text-foreground disabled:opacity-50"
                  >
                    <option value="">Select Chapter</option>
                    {chapters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
            </Card>

            {/* Step 2: Upload Question Image */}
            {selectedChapter && (
              <Card 
                className="p-6 border-blue-500/30 bg-blue-500/5"
                tabIndex={0}
                onPaste={async (e) => {
                  const items = e.clipboardData?.items;
                  if (items) {
                    for (const item of items) {
                      if (item.type.startsWith('image/')) {
                        e.preventDefault();
                        const file = item.getAsFile();
                        if (file) {
                          try {
                            toast.loading('Compressing...', { id: 'paste-upload' });
                            const compressedFile = await compressImage(file);
                            const formData = new FormData();
                            formData.append('file', compressedFile);
                            formData.append('category', 'questions');
                            toast.loading('Uploading...', { id: 'paste-upload' });
                            const res = await api.post('/admin/upload/image', formData);
                            setCurrentImageQuestion(prev => ({ ...prev, question_image: res.data.url }));
                            toast.success('Image pasted!', { id: 'paste-upload' });
                          } catch (err) {
                            toast.error('Failed to upload', { id: 'paste-upload' });
                          }
                        }
                        break;
                      }
                    }
                  }
                }}
              >
                <h4 className="font-semibold text-blue-400 mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm">2</span>
                  Upload Question
                  <span className="text-xs text-muted-foreground font-normal ml-2">
                    (Paste with Ctrl+V or click to browse)
                  </span>
                </h4>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Image Upload Zone */}
                  <div>
                    <div 
                      className="border-2 border-dashed border-border rounded-xl p-4 text-center hover:border-blue-500 transition-colors cursor-pointer relative min-h-[180px] flex items-center justify-center"
                      onClick={() => document.getElementById('question-image-input').click()}
                    >
                      {currentImageQuestion.question_image ? (
                        <div className="relative w-full">
                          <img 
                            src={typeof currentImageQuestion.question_image === 'string' 
                              ? (currentImageQuestion.question_image.startsWith('/') 
                                ? `http://localhost:8000${currentImageQuestion.question_image}` 
                                : currentImageQuestion.question_image)
                              : URL.createObjectURL(currentImageQuestion.question_image)} 
                            alt="Question" 
                            className="max-h-48 mx-auto rounded-lg"
                          />
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentImageQuestion(prev => ({ ...prev, question_image: null }));
                            }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div>
                          <Image size={36} className="mx-auto text-muted-foreground mb-2" />
                          <p className="text-muted-foreground text-sm">Click or <strong>Ctrl+V</strong> to paste</p>
                        </div>
                      )}
                    </div>
                    <input
                      id="question-image-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          try {
                            toast.loading('Compressing...');
                            const compressedFile = await compressImage(file);
                            const formData = new FormData();
                            formData.append('file', compressedFile);
                            formData.append('category', 'questions');
                            const res = await api.post('/admin/upload/image', formData);
                            setCurrentImageQuestion(prev => ({ ...prev, question_image: res.data.url }));
                            toast.success('Image uploaded!');
                          } catch (err) {
                            toast.error('Failed to upload image');
                          }
                        }
                      }}
                    />
                  </div>

                  {/* Quick Answer Selection */}
                  <div className="space-y-3">
                    {/* Correct Answer - Big Buttons */}
                    <div>
                      <label className="block text-sm text-muted-foreground mb-2">Correct Answer (click or press 1-4)</label>
                      <div className="flex gap-2">
                        {['A', 'B', 'C', 'D'].map((label, idx) => (
                          <button
                            key={label}
                            onClick={() => setCurrentImageQuestion(prev => ({ ...prev, correct_answer: label }))}
                            className={`flex-1 py-3 rounded-xl text-lg font-bold transition-all ${
                              currentImageQuestion.correct_answer === label 
                                ? 'bg-green-500 text-white scale-105 shadow-lg shadow-green-500/30' 
                                : 'bg-muted hover:bg-green-500/20 text-foreground'
                            }`}
                          >
                            {label}
                            <span className="text-xs opacity-50 ml-1">({idx + 1})</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Quick Categories */}
                    <div className="flex gap-3">
                      <label className="flex-1 flex items-center gap-2 p-3 rounded-xl border border-border cursor-pointer hover:border-blue-500 transition-colors">
                        <input
                          type="checkbox"
                          checked={currentImageQuestion.is_pyq}
                          onChange={(e) => setCurrentImageQuestion(prev => ({ ...prev, is_pyq: e.target.checked }))}
                          className="w-5 h-5 accent-blue-500"
                        />
                        <span className="text-blue-400 font-medium">📜 PYQ</span>
                      </label>
                      <label className="flex-1 flex items-center gap-2 p-3 rounded-xl border border-border cursor-pointer hover:border-amber-500 transition-colors">
                        <input
                          type="checkbox"
                          checked={currentImageQuestion.is_important}
                          onChange={(e) => setCurrentImageQuestion(prev => ({ ...prev, is_important: e.target.checked }))}
                          className="w-5 h-5 accent-amber-500"
                        />
                        <span className="text-amber-400 font-medium">⭐ Important</span>
                      </label>
                    </div>

                    {/* Year Input */}
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <Input
                          value={currentImageQuestion.year}
                          onChange={(e) => setCurrentImageQuestion(prev => ({ ...prev, year: e.target.value }))}
                          placeholder="Year (e.g., 2024)"
                          className="text-center"
                        />
                      </div>
                      <select
                        value={currentImageQuestion.question_type}
                        onChange={(e) => setCurrentImageQuestion(prev => ({ ...prev, question_type: e.target.value }))}
                        className="p-2 rounded-xl border border-border bg-background text-foreground"
                      >
                        <option value="MCQ">MCQ</option>
                        <option value="Numerical">Numerical</option>
                      </select>
                    </div>

                    {/* Submit Button */}
                    <Button
                      onClick={async () => {
                        if (!currentImageQuestion.question_image || !currentImageQuestion.correct_answer) {
                          toast.error('Please add question image and select correct answer');
                          return;
                        }

                        try {
                          setLoading(true);
                          await api.post('/admin/questions', {
                            exam_id: selectedExam,
                            subject_id: selectedSubject,
                            chapter_id: selectedChapter,
                            question_text: currentImageQuestion.question_text || '',
                            image_url: currentImageQuestion.question_image,
                            options: [
                              { label: 'A', text: '' },
                              { label: 'B', text: '' },
                              { label: 'C', text: '' },
                              { label: 'D', text: '' }
                            ],
                            correct_answer: currentImageQuestion.correct_answer,
                            explanation: currentImageQuestion.explanation || '',
                            year: currentImageQuestion.year || null,
                            is_pyq: currentImageQuestion.is_pyq,
                            is_important: currentImageQuestion.is_important,
                            question_type: currentImageQuestion.question_type,
                            difficulty: 'medium'
                          });

                          toast.success('Question added!');
                          setImageQuestions(prev => [...prev, currentImageQuestion]);
                          
                          // Reset form
                          setCurrentImageQuestion({
                            question_image: null,
                            question_text: '',
                            options: [
                              { label: 'A', text: '', image: null },
                              { label: 'B', text: '', image: null },
                              { label: 'C', text: '', image: null },
                              { label: 'D', text: '', image: null }
                            ],
                            correct_answer: 'A',
                            explanation: '',
                            solution_image: null,
                            year: '',
                            is_pyq: true,
                            is_important: false,
                            question_type: 'MCQ'
                          });
                          
                          loadStats();
                        } catch (error) {
                          toast.error('Failed to add: ' + (error.response?.data?.detail || error.message));
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading || !currentImageQuestion.question_image || !currentImageQuestion.correct_answer}
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                    >
                      {loading ? <Loader className="animate-spin mr-2" size={16} /> : <Check className="mr-2" size={16} />}
                      Add Question & Continue
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Web Scraper Tab - continued due to file length limitations */}
        {activeTab === 'scraper' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-start flex-wrap gap-4">
              <div>
                <h3 className="font-bold text-foreground text-xl flex items-center gap-2">
                  <Download className="text-cyan-500" size={24} />
                  Web Question Scraper
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Scrape questions from educational question banks and websites.
                </p>
              </div>
            </div>

            {/* Step 1: Select Location */}
            <Card className="p-6 border-cyan-500/30 bg-cyan-500/5">
              <h4 className="font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center text-sm">1</span>
                Select Target Location
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Exam</label>
                  <select
                    value={selectedExam || ''}
                    onChange={(e) => {
                      setSelectedExam(e.target.value);
                      setSelectedSubject(null);
                      setSelectedChapter(null);
                      if (e.target.value) {
                        api.get(`/exams/${e.target.value}/subjects`).then(res => setSubjects(res.data || []));
                      }
                    }}
                    className="w-full p-3 rounded-xl border border-border bg-background text-foreground"
                  >
                    <option value="">Select Exam</option>
                    {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Subject</label>
                  <select
                    value={selectedSubject || ''}
                    onChange={(e) => {
                      setSelectedSubject(e.target.value);
                      setSelectedChapter(null);
                      if (e.target.value) {
                        api.get(`/subjects/${e.target.value}/chapters`).then(res => setChapters(res.data || []));
                      }
                    }}
                    disabled={!selectedExam}
                    className="w-full p-3 rounded-xl border border-border bg-background text-foreground disabled:opacity-50"
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Chapter</label>
                  <select
                    value={selectedChapter || ''}
                    onChange={(e) => setSelectedChapter(e.target.value)}
                    disabled={!selectedSubject}
                    className="w-full p-3 rounded-xl border border-border bg-background text-foreground disabled:opacity-50"
                  >
                    <option value="">Select Chapter</option>
                    {chapters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
            </Card>

            {/* Step 2: Enter URL */}
            {selectedChapter && (
              <Card className="p-6 border-purple-500/30 bg-purple-500/5">
                <h4 className="font-semibold text-purple-400 mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-sm">2</span>
                  Enter Website URL
                </h4>
                
                {/* Quick Links */}
                <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                  <p className="text-sm text-green-400 font-medium mb-2">✅ Pro Tip: Use chapter-specific URLs for best results (400+ PYQs per chapter!)</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Find your specific chapter page with questions, then copy the URL from browser address bar
                  </p>
                </div>
                
                <div className="flex gap-4">
                  <Input
                    value={scraperUrl}
                    onChange={(e) => setScraperUrl(e.target.value)}
                    placeholder="https://example.com/questions/physics/chapter-name"
                    className="flex-1"
                  />
                  <Button
                    onClick={async () => {
                      if (!scraperUrl) {
                        toast.error('Please enter a URL');
                        return;
                      }
                      setScraperLoading(true);
                      setScrapedQuestions([]);
                      setFetchBatchProgress({ fetched: 0, total: 0, inProgress: false });
                      try {
                        const res = await api.post('/admin/scrape', { 
                          url: scraperUrl
                        });
                        setScrapedQuestions(res.data.questions || []);
                        if (res.data.questions?.length > 0) {
                          toast.success(`Found ${res.data.questions.length} questions via ${res.data.method}!`);
                        } else {
                          toast.info('No questions found. Please check the URL and try again.');
                        }
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to scrape');
                      } finally {
                        setScraperLoading(false);
                      }
                    }}
                    disabled={scraperLoading}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {scraperLoading ? <Loader className="animate-spin mr-2" size={16} /> : <Search className="mr-2" size={16} />}
                    Scrape
                  </Button>
                </div>
              </Card>
            )}

            {/* Step 3: Preview - showing simplified version due to space */}
            {scrapedQuestions.length > 0 && (
              <Card className="p-6 border-green-500/30 bg-green-500/5">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-green-400 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-sm">3</span>
                    Review & Import ({scrapedQuestions.length} questions)
                  </h4>
                  <Button
                    onClick={async () => {
                      if (!selectedExam || !selectedSubject || !selectedChapter) {
                        toast.error('Please select exam, subject and chapter');
                        return;
                      }

                      try {
                        setLoading(true);
                        const questionsToImport = scrapedQuestions.map(q => ({
                          ...q,
                          exam_id: selectedExam,
                          subject_id: selectedSubject,
                          chapter_id: selectedChapter
                        }));

                        const res = await api.post('/admin/questions/bulk', questionsToImport);
                        toast.success(`Imported ${res.data.count} questions!`);
                        setScrapedQuestions([]);
                        loadStats();
                      } catch (error) {
                        toast.error('Import failed: ' + (error.response?.data?.detail || error.message));
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    className="bg-green-500 hover:bg-green-600"
                  >
                    {loading ? <Loader className="animate-spin mr-2" size={16} /> : <Upload className="mr-2" size={16} />}
                    Import All
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  {scrapedQuestions.length} questions ready. Click Import All to add them to the database.
                </p>
              </Card>
            )}
          </div>
        )}

        {/* PDF/CSV Import Tab would continue here... Due to length constraints, I'll demonstrate the pattern with AI Parser */}
        
        {/* PDF/CSV Import Tab */}
        {activeTab === 'pdf-upload' && (
          <div className="space-y-6">
            <Card className="p-6 border border-border">
              <h3 className="font-bold text-foreground text-xl mb-4 flex items-center gap-2">
                <File className="text-red-500" size={24} />
                PDF & CSV Import
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Import questions from PDF files or CSV spreadsheets. CSV is faster and recommended for bulk imports.
              </p>
              
              <div className="space-y-4">
                <Card className="p-4 border-purple-500/30 bg-purple-500/5">
                  <h4 className="font-semibold mb-3">Select Destination</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <select
                      value={selectedExam || ''}
                      onChange={(e) => { setSelectedExam(e.target.value); loadSubjects(e.target.value); }}
                      className="p-3 rounded-lg bg-background border border-border text-sm"
                    >
                      <option value="">Select Exam *</option>
                      {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                    <select
                      value={selectedSubject || ''}
                      onChange={(e) => { setSelectedSubject(e.target.value); loadChapters(e.target.value); }}
                      disabled={!selectedExam}
                      className="p-3 rounded-lg bg-background border border-border text-sm disabled:opacity-50"
                    >
                      <option value="">Select Subject *</option>
                      {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <select
                      value={selectedChapter || ''}
                      onChange={(e) => setSelectedChapter(e.target.value)}
                      disabled={!selectedSubject}
                      className="p-3 rounded-lg bg-background border border-border text-sm disabled:opacity-50"
                    >
                      <option value="">Select Chapter *</option>
                      {chapters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </Card>

                <Card className="p-6 border-green-500/50 bg-green-500/5">
                  <h4 className="font-bold text-foreground mb-4 flex items-center gap-2">
                    <Zap className="text-green-500" size={20} />
                    CSV Import (Recommended - Fast!)
                  </h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Download the CSV template, fill it in Excel/Google Sheets, then upload. No AI limits!
                  </p>
                  <div className="flex gap-3">
                    <a 
                      href="http://localhost:8000/api/admin/questions/csv-template"
                      className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm flex items-center gap-2 hover:bg-green-600"
                    >
                      <Download size={16} />
                      Download Template
                    </a>
                    <label className="flex-1">
                      <div className="border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-green-500 hover:bg-green-500/5 transition-all">
                        {csvFile ? (
                          <div className="flex items-center justify-center gap-2">
                            <Check className="text-green-500" size={20} />
                            <span className="text-sm font-medium">{csvFile.name}</span>
                          </div>
                        ) : (
                          <div>
                            <Upload className="mx-auto text-muted-foreground mb-1" size={24} />
                            <p className="text-sm">Click to upload CSV</p>
                          </div>
                        )}
                      </div>
                      <input 
                        type="file" 
                        accept=".csv" 
                        onChange={(e) => setCsvFile(e.target.files[0])}
                        className="hidden" 
                      />
                    </label>
                    {csvFile && (
                      <Button
                        onClick={async () => {
                          if (!selectedExam || !selectedSubject || !selectedChapter) {
                            toast.error('Please select Exam, Subject and Chapter');
                            return;
                          }
                          
                          setCsvUploading(true);
                          try {
                            const formData = new FormData();
                            formData.append('file', csvFile);
                            formData.append('exam_id', selectedExam);
                            formData.append('subject_id', selectedSubject);
                            formData.append('chapter_id', selectedChapter);
                            
                            const res = await api.post('/admin/questions/import-csv', formData);
                            setCsvImportResult(res.data);
                            toast.success(`Imported ${res.data.imported} questions!`);
                            loadStats();
                          } catch (error) {
                            toast.error('Failed: ' + (error.response?.data?.detail || error.message));
                          } finally {
                            setCsvUploading(false);
                          }
                        }}
                        disabled={csvUploading}
                        className="bg-green-500 hover:bg-green-600"
                      >
                        {csvUploading ? <Loader className="animate-spin mr-2" size={16} /> : <Zap className="mr-2" size={16} />}
                        Import CSV
                      </Button>
                    )}
                  </div>
                </Card>
              </div>
            </Card>
          </div>
        )}

        {/* Bulk Import Tab */}
        {activeTab === 'bulk-import' && (
          <div className="space-y-6">
            <div className="flex justify-between items-start flex-wrap gap-4">
              <div>
                <h3 className="font-bold text-foreground text-xl flex items-center gap-2">
                  <Upload className="text-purple-500" size={24} />
                  Bulk Import Questions
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Import questions from JSON files with images support
                </p>
              </div>
              <Button variant="outline" onClick={downloadTemplate}>
                <Download size={16} className="mr-2" /> Download Template
              </Button>
            </div>

            {/* Selection Bar */}
            <Card className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/30">
              <p className="text-sm font-medium mb-3">Select where to import:</p>
              <div className="flex gap-4 flex-wrap">
                <select
                  value={selectedExam || ''}
                  onChange={(e) => { setSelectedExam(e.target.value); loadSubjects(e.target.value); }}
                  className="p-2 rounded-lg bg-background border border-border text-sm min-w-[200px]"
                >
                  <option value="">Select Exam *</option>
                  {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
                <select
                  value={selectedSubject || ''}
                  onChange={(e) => { setSelectedSubject(e.target.value); loadChapters(e.target.value); }}
                  className="p-2 rounded-lg bg-background border border-border text-sm min-w-[200px]"
                  disabled={!selectedExam}
                >
                  <option value="">Select Subject (optional)</option>
                  {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select
                  value={selectedChapter || ''}
                  onChange={(e) => setSelectedChapter(e.target.value)}
                  className="p-2 rounded-lg bg-background border border-border text-sm min-w-[200px]"
                  disabled={!selectedSubject}
                >
                  <option value="">Select Chapter (optional)</option>
                  {chapters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: JSON Input */}
              <Card className="p-6 border border-border">
                <h4 className="font-bold text-foreground mb-4 flex items-center gap-2">
                  <FileJson className="text-green-500" size={20} />
                  JSON Questions
                </h4>
                
                {/* File Upload */}
                <div className="mb-4">
                  <label className="block">
                    <div className="border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all">
                      <FileJson className="mx-auto text-muted-foreground mb-2" size={32} />
                      <p className="text-sm font-medium text-foreground">Upload JSON File</p>
                      <p className="text-xs text-muted-foreground mt-1">Click or drag & drop</p>
                    </div>
                    <input type="file" accept=".json" onChange={handleFileUpload} className="hidden" />
                  </label>
                </div>

                {/* Manual JSON Input */}
                <div className="mb-4">
                  <label className="text-sm font-medium text-foreground mb-2 block">Or paste JSON directly:</label>
                  <textarea
                    value={bulkImportData}
                    onChange={(e) => setBulkImportData(e.target.value)}
                    className="w-full p-3 rounded-lg bg-background border border-border text-sm font-mono min-h-[200px] focus:ring-2 focus:ring-primary"
                    placeholder='[{"question_text": "...", "options": [...], "correct_answer": "A", ...}]'
                  />
                </div>

                <Button onClick={handleJsonParse} className="w-full" disabled={!bulkImportData}>
                  <Eye size={16} className="mr-2" /> Parse & Preview
                </Button>
              </Card>

              {/* Right: Image Upload */}
              <Card className="p-6 border border-border">
                <h4 className="font-bold text-foreground mb-4 flex items-center gap-2">
                  <Image className="text-amber-500" size={20} />
                  Upload Images
                </h4>

                {/* Image Upload */}
                <div className="mb-4">
                  <label className="block">
                    <div className="border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all">
                      <Image className="mx-auto text-muted-foreground mb-2" size={32} />
                      <p className="text-sm font-medium text-foreground">Upload Images</p>
                      <p className="text-xs text-muted-foreground mt-1">PNG, JPG, GIF, WEBP (multiple)</p>
                    </div>
                    <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
                  </label>
                </div>

                {/* Upload Progress */}
                {importProgress.uploading && (
                  <div className="mb-4 p-3 bg-blue-500/10 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Loader className="animate-spin text-blue-500" size={16} />
                      <span className="text-sm">Uploading {importProgress.current}/{importProgress.total}...</span>
                    </div>
                    <div className="w-full h-2 bg-blue-200 rounded-full mt-2 overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 transition-all"
                        style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Uploaded Images List */}
                {uploadedImages.length > 0 && (
                  <div className="space-y-2 max-h-[250px] overflow-y-auto">
                    <p className="text-sm font-medium text-foreground">Uploaded ({uploadedImages.length}):</p>
                    {uploadedImages.map((img, i) => (
                      <div key={i} className="flex items-center justify-between text-xs p-2 bg-green-500/10 rounded-lg">
                        <span className="truncate flex-1">{img.original}</span>
                        <code className="ml-2 px-2 py-1 bg-background rounded text-green-600">{img.url}</code>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>

            {/* Preview */}
            {importPreview.length > 0 && (
              <Card className="p-6 border border-border">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-bold text-foreground flex items-center gap-2">
                    <Eye className="text-blue-500" size={20} />
                    Preview ({importPreview.length} questions)
                  </h4>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setImportPreview([])}>
                      <X size={16} className="mr-2" /> Clear
                    </Button>
                    <Button onClick={handleBulkImport} disabled={loading || !selectedExam} className="bg-green-500 hover:bg-green-600">
                      {loading ? <Loader size={16} className="animate-spin mr-2" /> : <Upload size={16} className="mr-2" />}
                      Import {importPreview.length} Questions
                    </Button>
                  </div>
                </div>

                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {importPreview.slice(0, 10).map((q, i) => (
                    <div key={i} className="p-4 bg-background rounded-xl border border-border">
                      <p className="font-medium text-foreground mb-2">
                        {i + 1}. {q.question_text?.substring(0, 150)}{q.question_text?.length > 150 ? '...' : ''}
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        <Badge className="bg-green-500/20 text-green-600 text-xs">Answer: {q.correct_answer}</Badge>
                        {q.difficulty && <Badge className="bg-gray-500/20 text-gray-600 text-xs">{q.difficulty}</Badge>}
                        {q.year && <Badge className="bg-purple-500/20 text-purple-600 text-xs">{q.year}</Badge>}
                      </div>
                    </div>
                  ))}
                  {importPreview.length > 10 && (
                    <p className="text-center text-muted-foreground text-sm">
                      ... and {importPreview.length - 10} more questions
                    </p>
                  )}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* AI Parser Tab */}
        {activeTab === 'ai-parser' && (
          <div className="space-y-6">
            <div className="flex justify-between items-start flex-wrap gap-4">
              <div>
                <h3 className="font-bold text-foreground text-xl flex items-center gap-2">
                  <Zap className="text-amber-500" size={24} />
                  AI Question Parser & Solution Generator
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Paste question text → AI parses & generates solutions automatically
                </p>
              </div>
            </div>

            {/* Selection Bar */}
            <Card className="p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30">
              <p className="text-sm font-medium mb-3">Select destination:</p>
              <div className="flex gap-4 flex-wrap">
                <select
                  value={selectedExam || ''}
                  onChange={(e) => { setSelectedExam(e.target.value); loadSubjects(e.target.value); }}
                  className="p-2 rounded-lg bg-background border border-border text-sm min-w-[200px]"
                >
                  <option value="">Select Exam *</option>
                  {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
                <select
                  value={selectedSubject || ''}
                  onChange={(e) => { setSelectedSubject(e.target.value); loadChapters(e.target.value); }}
                  className="p-2 rounded-lg bg-background border border-border text-sm min-w-[200px]"
                  disabled={!selectedExam}
                >
                  <option value="">Select Subject</option>
                  {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select
                  value={selectedChapter || ''}
                  onChange={(e) => setSelectedChapter(e.target.value)}
                  className="p-2 rounded-lg bg-background border border-border text-sm min-w-[200px]"
                  disabled={!selectedSubject}
                >
                  <option value="">Select Chapter</option>
                  {chapters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </Card>

            {/* Input Area */}
            <Card className="p-6 border border-border">
              <h4 className="font-bold text-foreground mb-4 flex items-center gap-2">
                <Type className="text-blue-500" size={20} />
                Paste Question Text
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                Copy questions directly from PDF, website, or anywhere. AI will parse them automatically.
                Separate multiple questions with blank lines or question numbers (Q1, Q2, 1., 2., etc.)
              </p>
              <textarea
                value={aiParserInput}
                onChange={(e) => setAiParserInput(e.target.value)}
                className="w-full p-4 rounded-xl bg-background border border-border text-sm min-h-[250px] font-mono focus:ring-2 focus:ring-primary"
                placeholder={`Example:\n\nQ28. To measure the temperature coefficient of resistivity α of a semiconductor...\n(A) 0.01 °C⁻¹\n(B) 0.02 °C⁻¹\n(C) -0.02 °C⁻¹\n(D) -0.01 °C⁻¹`}
              />
              <div className="flex gap-3 mt-4">
                <Button 
                  onClick={handleParseMultipleQuestions} 
                  disabled={aiProcessing || !aiParserInput.trim()}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  {aiProcessing ? <Loader className="animate-spin mr-2" size={16} /> : <Zap className="mr-2" size={16} />}
                  Parse with AI
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setAiParserInput('')}
                >
                  <X size={16} className="mr-2" /> Clear
                </Button>
              </div>
            </Card>

            {/* Parsed Questions */}
            {aiParsedQuestions.length > 0 && (
              <Card className="p-6 border border-border">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-bold text-foreground flex items-center gap-2">
                    <Check className="text-green-500" size={20} />
                    Parsed Questions ({aiParsedQuestions.length})
                  </h4>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={handleGenerateAllSolutions}
                      disabled={aiProcessing}
                      className="text-amber-600 border-amber-500 hover:bg-amber-500/10"
                    >
                      {aiProcessing ? <Loader className="animate-spin mr-2" size={16} /> : <Zap className="mr-2" size={16} />}
                      Generate All Solutions
                    </Button>
                    <Button 
                      onClick={handleImportParsedQuestions}
                      disabled={loading || !selectedExam}
                      className="bg-green-500 hover:bg-green-600"
                    >
                      {loading ? <Loader className="animate-spin mr-2" size={16} /> : <Upload className="mr-2" size={16} />}
                      Import All
                    </Button>
                  </div>
                </div>

                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {aiParsedQuestions.map((q, i) => (
                    <div key={i} className="p-4 bg-background rounded-xl border border-border">
                      <div className="flex justify-between items-start gap-4 mb-3">
                        <div className="flex-1">
                          <p className="font-medium text-foreground">
                            <span className="text-primary mr-2">Q{i + 1}.</span>
                            {q.question_text}
                          </p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {q.correct_answer && (
                            <Badge className="bg-green-500/20 text-green-600 text-xs">Ans: {q.correct_answer}</Badge>
                          )}
                        </div>
                      </div>

                      {/* Options */}
                      {q.options && q.options.length > 0 && (
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          {q.options.map((opt, j) => (
                            <div 
                              key={j} 
                              className={`p-2 rounded-lg text-sm ${
                                opt.label === q.correct_answer 
                                  ? 'bg-green-500/10 border border-green-500/30' 
                                  : 'bg-muted/50'
                              }`}
                            >
                              <span className="font-medium mr-2">{opt.label})</span>
                              <span dangerouslySetInnerHTML={{ __html: renderLatex(opt.text) }} />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Solution */}
                      {q.explanation && (
                        <div className="p-3 bg-blue-500/5 rounded-lg text-sm border border-blue-500/20">
                          <strong className="text-blue-600 block mb-2">Solution:</strong>
                          <pre className="whitespace-pre-wrap font-sans text-foreground">{q.explanation}</pre>
                        </div>
                      )}

                      {/* Generate solution for single question */}
                      {!q.explanation && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3"
                          onClick={async () => {
                            const solution = await generateSolutionForQuestion(q);
                            if (solution) {
                              const updated = [...aiParsedQuestions];
                              updated[i] = {
                                ...updated[i],
                                explanation: solution.explanation,
                                correct_answer: solution.correct_answer || updated[i].correct_answer
                              };
                              setAiParsedQuestions(updated);
                            }
                          }}
                          disabled={aiProcessing}
                        >
                          <Zap size={14} className="mr-1" /> Generate Solution
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}

          </div>{/* End Page Content */}
        </main>{/* End Main Content Area */}
      </div>{/* End Flex Container */}
    </Layout>
  );
};

export default EditorPanel;
